import { parseProject, type Project } from "@proof/core";
import { assertDocumentData } from "./document-data";
import {
  fromLegacy,
  normalizeLegacy,
  toLegacy,
  type LegacyEnvelope,
} from "./index";

/** One editable project, with the original dataset retained for a host round trip. */
export class EsmProjectSession {
  private original: LegacyEnvelope;
  private document: Project;
  readonly warnings: string[];

  constructor(value: unknown, options: { projectId?: string | number } = {}) {
    assertDocumentData(value);
    this.original = normalizeLegacy(value);
    const imported = fromLegacy(this.original);
    const selected =
      options.projectId === undefined
        ? imported.projects.length === 1
          ? imported.projects[0]
          : undefined
        : imported.projects.find(
            (p) =>
              String(p.legacy?.projectRow?.PROJECT_ID) ===
              String(options.projectId),
          );
    if (!selected)
      throw new Error(
        "Select an existing projectId when loading ESM project tables",
      );
    this.document = selected;
    this.warnings = [...imported.warnings];
  }

  get project(): Project {
    return structuredClone(this.document);
  }

  update(value: unknown): void {
    assertDocumentData(value);
    const next = parseProject(value);
    if (
      next.id !== this.document.id ||
      next.tenantId !== this.document.tenantId
    )
      throw new Error("The host project identity cannot be changed");
    // Identity and legacy source metadata belong to the host, not the editor.
    this.document = parseProject({ ...next, legacy: this.document.legacy });
  }

  exportESM(): LegacyEnvelope {
    const result = toLegacy([this.document], this.original);
    if (result._proofStudio && this.original._proofStudio)
      result._proofStudio.projects.push(
        ...structuredClone(
          this.original._proofStudio.projects.filter(
            (p) => p.id !== this.document.id,
          ),
        ),
      );
    return structuredClone(result);
  }
}
